import type { Character } from '../domain/character.ts';
import type { AttackProfile, Background, Item, Skill } from '../content/types.ts';
import { isCustomContentId } from '../domain/customContentId.ts';

/**
 * Working out which player-authored definitions a crawler needs.
 *
 * An export that carries a crawler but not the custom Skill it holds is not
 * portable - it is a crawler that arrives broken. This finds everything the
 * receiving installation would need.
 *
 * TWO problems, kept separate:
 *
 *  1. Which custom IDs does the saved crawler MENTION anywhere?
 *  2. What do those definitions themselves depend on?
 *
 * The second is real: a custom Background offers Skills, and those Skills may
 * themselves be custom. A future custom Item could depend on a Skill the same
 * way. That is why the closure below is driven by a list of RESOLVERS rather
 * than by hard-coded knowledge of Skills and Backgrounds.
 */

/**
 * Fields whose contents are free text the player typed, not references.
 *
 * The scan is deliberately blind: it walks the whole saved record and treats
 * ANY string in the custom namespace as a reference. That is what makes it
 * survive new fields - a custom ID stored somewhere nobody thought about is
 * still found. The cost is that free text containing something that looks like
 * an ID would be mistaken for a reference and would block the export, so the
 * two genuinely free-text fields are skipped by name.
 */
const FREE_TEXT_KEYS = new Set(['notes', 'reason', 'description']);

/**
 * Every custom content ID mentioned anywhere in the saved crawler.
 *
 * Walks the record rather than reading known fields, because custom IDs turn up
 * in more places than the obvious one:
 *
 *  - `skills`, the Skills the crawler holds now;
 *  - `origin.backgrounds`, the Backgrounds taken at creation AND the Skills
 *    chosen from each;
 *  - `advancementMarks`;
 *  - `changeLog[].change`, including `set-origin` records holding a whole
 *    creation record;
 *  - `changeLog[].undo`, which is the one that matters most. Undo may RE-ADD a
 *    Skill the crawler does not currently hold. Exporting only what is held
 *    would leave the audit trail unable to reverse itself after transfer.
 */
export function collectCustomReferences(character: Character): Set<string> {
  const found = new Set<string>();

  const walk = (value: unknown): void => {
    if (typeof value === 'string') {
      if (isCustomContentId(value)) found.add(value);
      return;
    }
    if (Array.isArray(value)) {
      for (const entry of value) walk(entry);
      return;
    }
    if (typeof value === 'object' && value !== null) {
      for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
        if (FREE_TEXT_KEYS.has(key)) continue;
        walk(entry);
      }
    }
  };

  walk(character);
  return found;
}

/**
 * Every custom content ID a crawler's REVERSIBLE HISTORY could bring back.
 *
 * `collectCustomReferences` above walks the whole record on purpose, which is
 * right for export and for deletion: it must never miss a reference. But it
 * cannot answer a different question that RULE DCC-CUSTOM-013 needs -
 * "could an Undo still restore this Item into an equipped state?" - because a
 * mention in current inventory and a mention in `changeLog[].undo` look
 * identical to it.
 *
 * That distinction is not academic. A crawler can OWN a custom Item while
 * stowed, with an audited Undo holding a `set-item` that would put it back on:
 *
 *   Sword (one-handed) and Shield both equipped
 *     -> unequip the Sword, which is now owned with equipped: 0
 *     -> the Undo restores it with equipped: 1
 *
 * The current layout is legal, so a structural edit checked against the present
 * state alone would pass - and then the Undo would recreate Warhammer beside a
 * Shield, which the Gear Slot rules forbid. Being currently owned must not
 * SUPPRESS the history check; it is a separate question with a separate answer.
 *
 * Only `changeLog[].undo` is walked. `changeLog[].change` describes what was
 * done, not what reversing it would do, and the crawler's current fields are
 * the present state rather than a reachable past one.
 */
export function collectReversibleReferences(character: Character): Set<string> {
  const found = new Set<string>();

  const walk = (value: unknown): void => {
    if (typeof value === 'string') {
      if (isCustomContentId(value)) found.add(value);
      return;
    }
    if (Array.isArray(value)) {
      for (const entry of value) walk(entry);
      return;
    }
    if (typeof value === 'object' && value !== null) {
      for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
        if (FREE_TEXT_KEYS.has(key)) continue;
        walk(entry);
      }
    }
  };

  for (const applied of character.changeLog) walk(applied.undo);
  return found;
}

/** The player-authored definitions an export carries. */
export interface PortableCustomContent {
  skills: Skill[];
  /** Optional executable companions for the Skills above; never independent seeds. */
  attacks: AttackProfile[];
  backgrounds: Background[];
  items: Item[];
}

export function emptyCustomContent(): PortableCustomContent {
  return { skills: [], attacks: [], backgrounds: [], items: [] };
}

/**
 * Where definitions are looked up, and what each one depends on in turn.
 *
 * One resolver per content type. Adding custom Items later means adding a
 * resolver here and a field to `PortableCustomContent`; the closure, the export
 * refusal and the import ordering all keep working unchanged.
 */
export interface ContentResolver {
  /** Finds the definition, or undefined when this installation lacks it. */
  find(id: string): unknown;
  /** Further custom IDs the definition itself references. */
  dependencies(definition: unknown): string[];
  /** Files the definition into the package. */
  collect(definition: unknown, into: PortableCustomContent): void;
}

export interface CustomContentSource {
  findSkill(id: string): Skill | undefined;
  /** AttackProfile is a Skill companion and is never resolved as its own seed. */
  findAttack(id: string): AttackProfile | undefined;
  findBackground(id: string): Background | undefined;
  findItem(id: string): Item | undefined;
}

export function resolversFor(source: CustomContentSource): ContentResolver[] {
  return [
    {
      find: (id) => source.findSkill(id),
      // A Skill depends on nothing else today. It is still asked, so a Skill
      // that later references something is handled without changing the loop.
      dependencies: () => [],
      collect: (definition, into) => {
        const skill = definition as Skill;
        into.skills.push(skill);
        const attack = source.findAttack(skill.id);
        // Official profiles are never redistributed. A custom companion travels
        // only because its custom Skill was selected by the dependency closure.
        if (attack?.source.book === 'custom') into.attacks.push(attack);
      },
    },
    {
      find: (id) => source.findBackground(id),
      // RULE DCC-CUSTOM-009 - a Background offers Skills and may hand over
      // Items, and either may be custom. BOTH are followed, or a Background
      // would arrive unable to compose.
      dependencies: (definition) => [
        ...(definition as Background).skillIds,
        ...((definition as Background).startingEquipment ?? []).map((g) => g.itemId),
      ],
      collect: (definition, into) => into.backgrounds.push(definition as Background),
    },
    {
      find: (id) => source.findItem(id),
      // A custom physical weapon may name a custom AttackProfile. The profile is
      // NOT an independent seed: follow the matching Skill ID, whose resolver
      // then carries its optional profile companion.
      dependencies: (definition) => {
        const item = definition as Item;
        return [
          ...(item.weaponAttackId === null ? [] : [item.weaponAttackId]),
          ...item.modifiers.flatMap((modifier) =>
            modifier.target.kind === 'skill-rank' ? [modifier.target.skillId] : [],
          ),
        ];
      },
      collect: (definition, into) => into.items.push(definition as Item),
    },
  ];
}

export interface DependencyClosure {
  content: PortableCustomContent;
  /** IDs the crawler references that this installation does not have. */
  missing: string[];
}

/**
 * Every definition needed, including the ones needed only by other definitions.
 *
 * Works to a fixed point: a Background pulls in its Skills, and if one of those
 * ever pulled in something else it would be followed too. IDs already collected
 * are not visited again, so a cycle terminates rather than looping.
 */
export function closeOverDependencies(
  seeds: Iterable<string>,
  resolvers: readonly ContentResolver[],
): DependencyClosure {
  const content = emptyCustomContent();
  const missing: string[] = [];
  const seen = new Set<string>();
  const queue = [...seeds];

  while (queue.length > 0) {
    const id = queue.shift() as string;
    if (seen.has(id)) continue;
    seen.add(id);

    let resolved = false;
    for (const resolver of resolvers) {
      const definition = resolver.find(id);
      if (definition === undefined) continue;
      resolver.collect(definition, content);
      for (const next of resolver.dependencies(definition)) {
        // Only custom IDs go back round: a Background offering the printed
        // Stealth needs nothing carried, because every installation has it.
        if (isCustomContentId(next) && !seen.has(next)) queue.push(next);
      }
      resolved = true;
      break;
    }
    if (!resolved) missing.push(id);
  }

  // Stable order, so the same crawler exports byte-identically twice.
  content.skills.sort((a, b) => a.id.localeCompare(b.id));
  content.attacks.sort((a, b) => a.id.localeCompare(b.id));
  content.backgrounds.sort((a, b) => a.id.localeCompare(b.id));
  content.items.sort((a, b) => a.id.localeCompare(b.id));
  missing.sort();

  return { content, missing };
}

/** The definitions this crawler needs carried with it. */
export function collectCustomDependencies(
  character: Character,
  source: CustomContentSource,
): DependencyClosure {
  return closeOverDependencies(collectCustomReferences(character), resolversFor(source));
}
